import React, { useEffect, useRef, useContext, useState } from "react";
import { 
  Box, 
  Paper, 
  Stack, 
  Avatar, 
  CircularProgress, 
  Typography, 
  IconButton, 
  Tooltip,
  TextField,
  Button
} from "@mui/material";
import { 
  ContentCopy, 
  Edit, 
  DeleteOutline, 
  ThumbUp, 
  ThumbDown,
  ThumbUpOutlined,
  ThumbDownOutlined,
  Check,
  Close,
  StopCircleOutlined,
  Refresh
} from "@mui/icons-material";
import { Divider } from "@mui/material";
import { motion } from "framer-motion";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeHighlight from "rehype-highlight";
import "highlight.js/styles/github.css"; // for light syntax highlighting
import { ChatContext } from "../context/chatcontext";

export default function ChatWindow({ messages = [] }) {
  const chatEndRef = useRef(null);
  const { 
    isProcessing, 
    processingMessage, 
    editMessage, 
    deleteMessage, 
    rateResponse,
    regenerateResponse,
    stopGeneration
  } = useContext(ChatContext);
  
  const [editingIndex, setEditingIndex] = useState(null);
  const [editContent, setEditContent] = useState("");

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isProcessing, processingMessage]);

  const handleCopy = (content) => {
    navigator.clipboard.writeText(content);
    // You could add a toast notification here
  };

  const startEditing = (idx, content) => {
    setEditingIndex(idx);
    setEditContent(content);
  };

  const cancelEditing = () => {
    setEditingIndex(null);
    setEditContent("");
  };

  const saveEdit = (idx) => {
    if (editContent.trim()) {
      editMessage(idx, editContent);
    }
    setEditingIndex(null);
    setEditContent("");
  };

  return (
    <Box
      sx={{
        flex: 1,
        overflowY: "auto",
        px: 4,
        py: 3,
        bgcolor: "#FFFFFF",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {messages.length === 0 ? (
        <Box
          sx={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "grey.700",
          }}
        >
          Start a new chat to begin!
        </Box>
      ) : (
        <Stack spacing={2}>
          {messages.map((msg, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.2 }}
            >
              <Stack
                direction={msg.role === "user" ? "row-reverse" : "row"}
                alignItems="flex-start"
                spacing={1.5}
              >
                {/* Avatar */}
                <Avatar
                  sx={{
                    bgcolor:
                      msg.role === "user" ? "#87CEEB" : "#F0F8FF",
                    color: msg.role === "user" ? "#FFFFFF" : "#000000",
                    width: 32,
                    height: 32,
                    fontSize: 14,
                    border: msg.role === "user" ? "none" : "1px solid #87CEEB",
                  }}
                >
                  {msg.role === "user" ? "U" : "AI"}
                </Avatar>

                {/* Message Bubble */}
                <Box sx={{ maxWidth: "75%", position: "relative" }}>
                  {editingIndex === idx ? (
                    <Paper
                      elevation={3}
                      sx={{
                        p: 2,
                        bgcolor: msg.role === "user" ? "#87CEEB" : "#F0F8FF",
                        borderRadius: 3,
                        border: "1px solid #87CEEB",
                      }}
                    >
                      <TextField
                        fullWidth
                        multiline
                        variant="standard"
                        value={editContent}
                        onChange={(e) => setEditContent(e.target.value)}
                        sx={{ 
                          "& .MuiInputBase-root": { 
                            color: msg.role === "user" ? "#FFFFFF" : "#000000",
                            fontSize: "0.95rem"
                          } 
                        }}
                      />
                      <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 1 }}>
                        <IconButton size="small" onClick={() => saveEdit(idx)} sx={{ color: msg.role === "user" ? "#FFFFFF" : "#000000" }}>
                          <Check fontSize="small" />
                        </IconButton>
                        <IconButton size="small" onClick={cancelEditing} sx={{ color: msg.role === "user" ? "#FFFFFF" : "#000000" }}>
                          <Close fontSize="small" />
                        </IconButton>
                      </Stack>
                    </Paper>
                  ) : (
                    <>
                      <Paper
                        elevation={3}
                        sx={{
                          p: 2,
                          bgcolor: msg.role === "user" ? "#87CEEB" : "#F0F8FF",
                          color: msg.role === "user" ? "#FFFFFF" : "#000000",
                          borderRadius: 3,
                          borderBottomRightRadius: msg.role === "user" ? 0 : 3,
                          borderBottomLeftRadius: msg.role === "assistant" ? 0 : 3,
                          whiteSpace: "pre-wrap",
                          wordBreak: "break-word",
                          fontSize: "0.95rem",
                          border: msg.role === "assistant" ? "1px solid #87CEEB" : "none",
                        }}
                      >
                        <ReactMarkdown
                          remarkPlugins={[remarkGfm]}
                          rehypePlugins={[rehypeHighlight]}
                          components={{
                            p: ({ children }) => <span style={{ lineHeight: 1.6 }}>{children}</span>,
                            code: ({ node, inline, className, children, ...props }) =>
                              inline ? (
                                <code
                                  style={{
                                    backgroundColor: "rgba(0,0,0,0.05)",
                                    padding: "2px 6px",
                                    borderRadius: "4px",
                                  }}
                                  {...props}
                                >
                                  {children}
                                </code>
                              ) : (
                                <pre
                                  style={{
                                    backgroundColor: "#F5F5F5",
                                    padding: "10px",
                                    borderRadius: "6px",
                                    overflowX: "auto",
                                    border: "1px solid #E0E0E0",
                                  }}
                                >
                                  <code className={className} {...props}>
                                    {children}
                                  </code>
                                </pre>
                              ),
                          }}
                        >
                          {msg.content}
                        </ReactMarkdown>
                      </Paper>

                      {/* Actions */}
                      <Stack
                        className="msg-actions"
                        direction="row"
                        spacing={0.5}
                        sx={{
                          opacity: 1,
                          transition: "opacity 0.2s",
                          position: "absolute",
                          bottom: -28,
                          [msg.role === "user" ? "right" : "left"]: 0,
                          bgcolor: "rgba(0,0,0,0.6)",
                          borderRadius: 1,
                          px: 0.5,
                          zIndex: 1,
                        }}
                      >
                        <Tooltip title="Copy">
                          <IconButton size="small" onClick={() => handleCopy(msg.content)} sx={{ color: "grey.400", "&:hover": { color: "#87CEEB" } }}>
                            <ContentCopy fontSize="inherit" />
                          </IconButton>
                        </Tooltip>

                        {msg.role === "user" && (
                          <>
                            <Tooltip title="Edit">
                              <IconButton size="small" onClick={() => startEditing(idx, msg.content)} sx={{ color: "grey.400", "&:hover": { color: "#87CEEB" } }}>
                                <Edit fontSize="inherit" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete">
                              <IconButton size="small" onClick={() => deleteMessage(idx)} sx={{ color: "grey.400", "&:hover": { color: "#ff4444" } }}>
                                <DeleteOutline fontSize="inherit" />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                        
                        {msg.role === "assistant" && (
                          <>
                            <Tooltip title="Regenerate">
                              <IconButton size="small" onClick={() => regenerateResponse(idx)} sx={{ color: "grey.400", "&:hover": { color: "#87CEEB" } }}>
                                <Refresh fontSize="inherit" />
                              </IconButton>
                            </Tooltip>
                            <Divider orientation="vertical" flexItem sx={{ bgcolor: "grey.700", my: 0.5 }} />
                            <Tooltip title="Thumbs Up">
                              <IconButton size="small" onClick={() => rateResponse(idx, 1)} sx={{ color: msg.rating === 1 ? "#87CEEB" : "grey.400", "&:hover": { color: "#87CEEB" } }}>
                                {msg.rating === 1 ? <ThumbUp fontSize="inherit" /> : <ThumbUpOutlined fontSize="inherit" />}
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Thumbs Down">
                              <IconButton size="small" onClick={() => rateResponse(idx, -1)} sx={{ color: msg.rating === -1 ? "#ff4444" : "grey.400", "&:hover": { color: "#ff4444" } }}>
                                {msg.rating === -1 ? <ThumbDown fontSize="inherit" /> : <ThumbDownOutlined fontSize="inherit" />}
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                      </Stack>
                    </>
                  )}
                </Box>
              </Stack>
            </motion.div>
          ))}
          
          {/* 🧠 Processing Message Indicator */}
          {isProcessing && processingMessage && (
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.2 }}
            >
              <Stack
                direction="row"
                alignItems="center"
                spacing={1.5}
              >
                <Avatar
                  sx={{
                    bgcolor: "#F0F8FF",
                    color: "#000000",
                    width: 32,
                    height: 32,
                    fontSize: 14,
                    border: "1px solid #87CEEB",
                  }}
                >
                  AI
                </Avatar>
                <Paper
                  elevation={3}
                  sx={{
                    p: 2,
                    maxWidth: "75%",
                    bgcolor: "#F0F8FF",
                    color: "#000000",
                    borderRadius: 3,
                    borderBottomLeftRadius: 0,
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    border: "1px solid #87CEEB",
                    position: "relative",
                  }}
                >
                  <CircularProgress size={18} sx={{ color: "#87CEEB" }} />
                  <Typography sx={{ fontSize: "0.95rem", fontStyle: "italic" }}>
                    {processingMessage}
                  </Typography>
                  <Tooltip title="Stop Generation">
                    <IconButton 
                      size="small" 
                      onClick={stopGeneration}
                      sx={{ 
                        color: "#000000", 
                        ml: 1,
                        "&:hover": { bgcolor: "rgba(135, 206, 235, 0.2)" } 
                      }}
                    >
                      <StopCircleOutlined fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Paper>
              </Stack>
            </motion.div>
          )}
        </Stack>
      )}
      <div ref={chatEndRef} />
    </Box>
  );
}
